import {Alert, Button, Card, DataStatus, Dialog} from 'platform/components';
import {Box, Grid, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import {
  useGetModelYearReadinessQuery,
  type ModelYearReadinessCheckResponseBody,
  type ModelYearReadinessSeverity,
} from '@dms/api/new-vehicle';

import {tabLabels, type TabId} from '../constants/tabLabels';
import {failingChecks, t} from '../utils/catalogueUi';

export type ReadinessFailure = {code: string; message: string; tab: TabId};

const readinessSummary = (isReady: boolean, hasWarnings: boolean) =>
  match([isReady, hasWarnings])
    .with([false, Pattern._], () => ({
      flag: t('entity.newVehicle.catalogue.notReady', 'Not ready'),
      hint: t(
        'entity.newVehicle.catalogue.notReadyHint',
        'Fix the items below before this model year can be published.'
      ),
      isClear: false,
    }))
    .with([true, true], () => ({
      flag: t('entity.newVehicle.catalogue.readyWithWarnings', 'Ready, with warnings'),
      hint: t(
        'entity.newVehicle.catalogue.readyWithWarningsHint',
        'Ready to publish. Check the warnings below first.'
      ),
      isClear: false,
    }))
    .otherwise(() => ({
      flag: t('entity.newVehicle.catalogue.readyToPublish', 'Ready to publish'),
      hint: t(
        'entity.newVehicle.catalogue.readyHint',
        'Everything is in place. Publish to make this model year orderable.'
      ),
      isClear: true,
    }));

/** A failing check's label with the lines naming what fails it, such as the duplicated code. */
function FailingCheckLabel(props: {
  severity: ModelYearReadinessSeverity;
  label: string;
  details: string[];
}) {
  const isWarning = props.severity === 'warning';
  return (
    <VStack spacing={1}>
      <HStack spacing={2} align="center">
        <Icon
          value={isWarning ? 'alert/warning' : 'alert/error_outline'}
          color={isWarning ? 'severity.warning' : 'severity.danger'}
          size={4}
        />
        <Text size="small">{props.label}</Text>
      </HStack>
      <Show when={props.details.length > 0}>
        <Box paddingLeft={6}>
          <VStack spacing={1}>
            {props.details.map((detail) => (
              <Text key={detail} size="xSmall" color="secondary">
                {detail}
              </Text>
            ))}
          </VStack>
        </Box>
      </Show>
    </VStack>
  );
}

export function PublishChecklist(props: {modelYearId: string; onTab: (tab: TabId) => void}) {
  const readiness = useGetModelYearReadinessQuery({modelYearId: props.modelYearId});
  const checks = readiness.data?.checks ?? [];
  const blocking = failingChecks(checks, 'blocking');
  const warnings = failingChecks(checks, 'warning');
  const failing = [...blocking, ...warnings];
  const passing = checks.filter((check) => check.passed);
  const summary = readinessSummary(readiness.data?.ready ?? false, warnings.length > 0);
  const [showPassed, setShowPassed] = useState(true);

  return (
    <Card
      title={t('entity.newVehicle.catalogue.publishReadiness', 'Publish readiness')}
      flags={[
        {
          label: summary.flag,
          colorScheme: summary.isClear ? 'green' : 'orange',
          isSubtle: true,
        },
      ]}
    >
      <DataStatus
        isLoading={readiness.isLoading}
        isError={readiness.isError}
        isEmpty={!checks.length}
      >
        <VStack spacing={4}>
          <HStack spacing={3} align="center">
            <Icon
              value={summary.isClear ? 'action/check_circle' : 'alert/error_outline'}
              color={summary.isClear ? 'severity.success' : 'severity.warning'}
              size={6}
            />
            <VStack spacing={0}>
              <Text alternative>{summary.hint}</Text>
              <Text size="small" color="secondary">
                {t(
                  'entity.newVehicle.catalogue.checksPassedCount',
                  '{passed} of {total} checks passed',
                  {passed: passing.length, total: checks.length}
                )}
              </Text>
            </VStack>
          </HStack>
          <CheckSegments aria-hidden>
            {/* Failing first, so what needs attention sits at the start of the bar. */}
            {[...failing, ...passing].map((check) => (
              <CheckSegment key={check.code} $tone={check.passed ? 'passed' : check.severity} />
            ))}
          </CheckSegments>
          <Show when={failing.length > 0}>
            <VStack spacing={2}>
              <Text size="xSmall" color="secondary" alternative>
                {t('entity.newVehicle.catalogue.needsAttention', 'Needs attention')}
              </Text>
              {failing.map((check) => (
                <FailingCheckRow
                  key={check.code}
                  $severity={check.severity}
                  data-testid={`newVehicleCatalogue-readinessCheck-${check.code}`}
                >
                  <FailingCheckLabel
                    severity={check.severity}
                    label={check.label}
                    details={check.details}
                  />
                  <Button
                    variant="link"
                    size="small"
                    rightIcon="navigation/chevron_right"
                    title={t('entity.newVehicle.catalogue.fixIn', 'Fix in {tab}', {
                      tab: tabLabels[check.tab],
                    })}
                    onClick={() => props.onTab(check.tab)}
                  />
                </FailingCheckRow>
              ))}
            </VStack>
          </Show>
          <Show when={passing.length > 0}>
            <VStack spacing={2}>
              {/* Keeps the toggle at its own width; the checklist below spans the card. */}
              <HStack>
                <Button
                  variant="ghostLink"
                  size="small"
                  rightIcon={showPassed ? 'navigation/expand_less' : 'navigation/expand_more'}
                  title={t('entity.newVehicle.catalogue.passedChecks', 'Passed checks ({count})', {
                    count: passing.length,
                  })}
                  onClick={() => setShowPassed(!showPassed)}
                />
              </HStack>
              <Show when={showPassed}>
                <Grid columns={[1, 1, 2]} spacing={2}>
                  {passing.map((check) => (
                    <HStack key={check.code} spacing={2} align="flex-start">
                      <Icon value="navigation/check" color="severity.success" size={4} />
                      <Text size="small" color="secondary">
                        {check.label}
                      </Text>
                    </HStack>
                  ))}
                </Grid>
              </Show>
            </VStack>
          </Show>
        </VStack>
      </DataStatus>
    </Card>
  );
}

const CHECK_PALETTE = {passed: 'green', blocking: 'red', warning: 'orange'} as const;

const CheckSegments = styled.div`
  display: flex;
  gap: ${({theme}) => theme.getSize(1)};
`;

const CheckSegment = styled.span<{$tone: keyof typeof CHECK_PALETTE}>`
  flex: 1;
  height: ${({theme}) => theme.getSize(1)};
  border-radius: ${({theme}) => theme.radii.circular};
  background-color: ${({theme, $tone}) => theme.colors.palettes[CHECK_PALETTE[$tone]][60][100]};
`;

const FailingCheckRow = styled.div<{$severity: ModelYearReadinessSeverity}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({theme}) => theme.getSize(3)};
  padding: ${({theme}) => `${theme.getSize(2)} ${theme.getSize(3)}`};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme, $severity}) =>
    theme.colors.palettes[CHECK_PALETTE[$severity]][10][100]};
`;

export function PublishBlockedDialog(props: {
  failures: ReadinessFailure[];
  checks: Pick<ModelYearReadinessCheckResponseBody, 'code' | 'details'>[];
  onClose: () => void;
  onTab: (tab: TabId) => void;
}) {
  return (
    <Dialog
      isOpen={props.failures.length > 0}
      onClose={props.onClose}
      title={t('entity.newVehicle.catalogue.publishBlocked', 'Publishing is blocked')}
      buttons={[
        {title: t('general.actions.close', 'Close'), variant: 'secondary', onClick: props.onClose},
      ]}
    >
      <VStack spacing={3}>
        {props.failures.map((failure) => (
          <HStack key={failure.code} justify="space-between" align="center">
            <FailingCheckLabel
              severity="blocking"
              label={failure.message}
              details={props.checks.find((check) => check.code === failure.code)?.details ?? []}
            />
            <Button
              variant="link"
              size="small"
              title={`${t('general.actions.open', 'Open')} ${tabLabels[failure.tab]}`}
              onClick={() => {
                props.onClose();
                props.onTab(failure.tab);
              }}
            />
          </HStack>
        ))}
      </VStack>
    </Dialog>
  );
}

export function PublishWarningsDialog(props: {
  isOpen: boolean;
  warnings: Pick<ModelYearReadinessCheckResponseBody, 'code' | 'label' | 'tab' | 'details'>[];
  onClose: () => void;
  onPublish: () => void;
  onTab: (tab: TabId) => void;
}) {
  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={t('entity.newVehicle.catalogue.publishWithWarnings', 'Publish with warnings?')}
      buttons={[
        {
          title: t('general.actions.cancel', 'Cancel'),
          variant: 'secondary',
          onClick: props.onClose,
        },
        {
          title: t('entity.newVehicle.catalogue.publishAnyway', 'Publish anyway'),
          variant: 'primary',
          onClick: () => {
            props.onClose();
            props.onPublish();
          },
        },
      ]}
    >
      <VStack spacing={3}>
        {props.warnings.map((check) => (
          <Alert
            key={check.code}
            variant="warning"
            layout="detailed"
            title={check.label}
            message={check.details}
            actions={[
              {
                title: `${t('general.actions.open', 'Open')} ${tabLabels[check.tab]}`,
                variant: 'link',
                size: 'small',
                onClick: () => {
                  props.onClose();
                  props.onTab(check.tab);
                },
              },
            ]}
          />
        ))}
      </VStack>
    </Dialog>
  );
}
